import * as Sentry from "@sentry/react";

import {
  mapValidationErrorsDitto,
  nullableFormBooleanToNullableBoolean,
} from "Utils/formUtils";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { HOUSE_CONTENTS_ABOUT_STEP, HOUSE_CONTENTS_INTRO_STEP } from "..";
import {
  HouseContentsProductDetailsFormValues,
  HouseContentsProductIntroFormValues,
} from "../houseContentsFormValuesInterfaces";

import { BackendProductInfo } from "Components/Features/CheckPrice/Products/productConstants";
import { ProductProps } from "Components/Features/CheckPrice/Products/productInterfaces";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductDetailsTile } from "Components/Features/CheckPrice/Tiles/ProductDetailsTile";
import { BackendErrorAlert } from "Components/Form/CommonFormElements/BackendErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { RecentDamages } from "Components/Form/RecentDamages";
import { DamageRecentYears } from "Components/Form/RecentDamages/recentDamagesInterfaces";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { ReduxState } from "Store/rootReducer";
import React from "react";
import { BuildingMaterial } from "../../FormComponentsHouseProducts/BuildingMaterial";
import { CoverageAndCalculator } from "../../FormComponentsHouseProducts/CoverageAndCalculator";

import { useTexts } from "Components/TextContext";
import { Data } from "Components/TextContext/textInterfaces";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { isFullRental } from "../houseContentsUtils";
import { validationSchema } from "./houseContentsProductDetailsValidation";

interface Props extends ProductProps {
  shortTitleTexts: Data[];
}

export const HouseContentsProductDetails = ({
  updatePriceRequest,
  shortTitleTexts,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();

  const initialFormState = useFormState<HouseContentsProductDetailsFormValues>(
    HOUSE_CONTENTS_ABOUT_STEP.id,
    initialFormValues,
  );

  const { getText } = useTexts();
  const {
    priceCalculation: { priceCalculationResult },
  } = useSelector((state: ReduxState) => state);

  const houseContentsProductIntroFormValues = useFormState(
    HOUSE_CONTENTS_INTRO_STEP.id,
  ) as HouseContentsProductIntroFormValues;

  const fullRental = isFullRental(houseContentsProductIntroFormValues);

  const handleSubmit = (
    formValues: HouseContentsProductDetailsFormValues,
  ): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: HOUSE_CONTENTS_ABOUT_STEP.id,
        formState: formValues,
      }),
    );
    updatePriceRequest({
      productPriceUrl: BackendProductInfo.HOUSE_CONTENTS.priceReqUrl,
      stepForwardOnSuccess: true,
      updatedProductPriceRequestInfo: {
        coverageAmount: formValues.coverageAmount,
        fireResistantBuildingMaterial:
          nullableFormBooleanToNullableBoolean(
            formValues.fireResistantBuildingMaterial,
          ) ?? false,
        injuries: formValues.injuries === DamageRecentYears.NONE ? 0 : 1,
        injuryYear: formValues.injuryYear
          ? parseInt(formValues.injuryYear)
          : null,
      },
    });
  };

  const calculate = (
    calculatedCoverage: number,
    formProps: FormikProps<HouseContentsProductDetailsFormValues>,
  ): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: HOUSE_CONTENTS_ABOUT_STEP.id,
        formState: {
          ...formProps.values,
          coverageAmount: calculatedCoverage,
        },
      }),
    );
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductDetails - ${INSURANCE_NAME_VALUE.HOUSE_CONTENTS}`,
        );
      }}
    >
      <ProductDetailsTile
        header={HOUSE_CONTENTS_ABOUT_STEP.title}
        validationSchema={validationSchema()}
        initialValues={{
          ...initialFormState,
          fullRental,
        }}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<HouseContentsProductDetailsFormValues>) => {
          return (
            <Form>
              <BuildingMaterial formProps={formProps} />
              <CoverageAndCalculator
                name="coverageAmount"
                label={getText("housecontentsform.label.coverageamount")}
                hint={getText("housecontentsform.hint.coverageamount")}
                formProps={formProps}
                productId={ProductBackendName.HOUSE_CONTENTS}
                handleCalculatorSubmit={(calculateRecommendation) =>
                  calculate(calculateRecommendation, formProps)
                }
                isFullRental={fullRental}
              />

              <RecentDamages
                formProps={formProps}
                productName={ProductBackendName.HOUSE_CONTENTS}
              />
              <BackendErrorAlert priceCalculation={priceCalculationResult} />

              <ErrorsSummedUp
                errors={mapValidationErrorsDitto(
                  formProps.errors,
                  shortTitleTexts,
                )}
                isVisible
              />
              <ScrollToFirstError />
              <NavigationButtons isLoading={priceCalculationResult.isLoading} />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.HOUSE_CONTENTS}
                stepId={HOUSE_CONTENTS_ABOUT_STEP.id}
              />
            </Form>
          );
        }}
      </ProductDetailsTile>
    </Sentry.ErrorBoundary>
  );
};

const initialFormValues = {
  coverageAmount: undefined,
  fireResistantBuildingMaterial: undefined,
  fullRental: undefined,
  injuries: undefined,
  injuryYear: undefined,
};
